<template>
  <EquivalentExtractionCopyBase
    :subtitle="'Browse finalized study designs from equivalent reviews.'"
    :cache-key="study.id"
    :fetch-studies="fetchStudies"
    @copy="emit('copyStudyDesign', $event)"
  >
    <template #preview="{ extraction }">
      <div v-if="extraction.value" class="mt-2">
        <StudyDesignThumbnail
          :populations="extraction.value.populations ?? []"
          :arms="extraction.value.arms ?? []"
          :periods="extraction.value.periods ?? []"
          :intervention-definitions="extraction.value.interventionDefinitions ?? []"
          class="h-36 cursor-pointer"
          @click="openFullscreen(extraction.value)"
        />
      </div>
      <div v-else class="text-sm text-gray-500 italic">No value extracted</div>
    </template>
  </EquivalentExtractionCopyBase>

  <Teleport to="body">
    <div v-if="isFullscreen" class="fixed inset-0 flex flex-col bg-white z-100000">
      <div class="flex items-center px-4 py-2 border-b border-gray-200">
        <button
          class="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors px-2 py-1 rounded hover:bg-slate-100"
          @click="isFullscreen = false"
        >
          <i-material-symbols-arrow-back class="w-4 h-4" />
          Back
        </button>
      </div>
      <VisualStudyDesignBuilder
        v-if="fullscreenValue"
        :model-value="fullscreenValue"
        mode="select"
        class="flex-1 min-h-0"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { injectStrict } from "@app/utils/injectStrict";
import { EvidenceServiceApiKey } from "@app/injectionKeys";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import type { StudyDesignValue } from "@core/domain/types/attributeValue.type";
import { StudyDesignThumbnail, VisualStudyDesignBuilder } from "visual-study-design-builder";
import "visual-study-design-builder/style.css";
import EquivalentExtractionCopyBase from "./EquivalentExtractionCopyBase.vue";
import type { EquivalentStudyEntry } from "./EquivalentExtractionCopyBase.vue";

const isFullscreen = ref(false);
const fullscreenValue = ref<StudyDesignValue | null>(null);

function openFullscreen(value: StudyDesignValue) {
  fullscreenValue.value = value;
  isFullscreen.value = true;
}

const props = defineProps<{
  study: ReviewItem;
}>();

const emit = defineEmits<{
  copyStudyDesign: [value: StudyDesignValue];
}>();

const evidenceService = injectStrict(EvidenceServiceApiKey);

async function fetchStudies(): Promise<EquivalentStudyEntry<StudyDesignValue>[]> {
  const response = await evidenceService.api.reviewItemsControllerGetEquivalentFinalizedStudyDesign(
    props.study.id,
  );
  return response.data.map((s) => ({
    reviewSeriesName: s.reviewSeriesName,
    study: {
      id: s.id,
      title: s.title,
      extractions: s.extractions.map((e) => ({
        label: e.label,
        attributeStructureId: e.attributeStructureId,
        value: e.value as StudyDesignValue,
      })),
    },
  }));
}
</script>
